import { A4_PAGE_WIDTH } from '@/constants';
import { MeasureWrapper } from './MeasureWrapper';
import type { ReportFootnote, ReportFootnotePlan } from './footnotes';
import { getFootnoteMeasurementId, REPORT_FOOTNOTE_OVERHEAD_MEASUREMENT_ID } from './footnotes';

type ReportFootnoteMarkerProps = {
    footnotes?: ReportFootnote[];
    className?: string;
};

export function ReportFootnoteMarker({ footnotes, className = '' }: ReportFootnoteMarkerProps) {
    if (!footnotes?.length) return null;

    return (
        <sup className={`text-[10px] font-medium leading-none text-slate-500 ${className}`}>
            {footnotes.map((footnote) => footnote.number).join(', ')}
        </sup>
    );
}

function ReportFootnoteEntry({ footnote }: { footnote: ReportFootnote }) {
    return (
        <div className="flex items-start gap-1.5 text-[10px] leading-snug text-slate-600">
            <sup className="min-w-2 pt-0.5 text-[8px] font-medium leading-none text-slate-500">
                {footnote.number}
            </sup>
            <span className="flex-1">{footnote.text}</span>
        </div>
    );
}

type ReportPageFootnotesProps = {
    footnotes: ReportFootnote[];
};

export function ReportPageFootnotes({ footnotes }: ReportPageFootnotesProps) {
    if (footnotes.length === 0) return null;

    return (
        <div className="mx-12 border-t border-slate-300 pb-1 pt-2">
            <div className="space-y-1">
                {footnotes.map((footnote) => (
                    <ReportFootnoteEntry key={footnote.id} footnote={footnote} />
                ))}
            </div>
        </div>
    );
}

type ReportFootnoteMeasurerProps = {
    footnotePlan: ReportFootnotePlan;
};

export function ReportFootnoteMeasurer({ footnotePlan }: ReportFootnoteMeasurerProps) {
    if (footnotePlan.all.length === 0) return null;

    return (
        <div
            aria-hidden="true"
            style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: A4_PAGE_WIDTH,
                visibility: 'hidden',
                pointerEvents: 'none',
                zIndex: -1000,
            }}
        >
            <div className="px-12">
                <MeasureWrapper id={REPORT_FOOTNOTE_OVERHEAD_MEASUREMENT_ID}>
                    <div className="border-t border-slate-300 pb-1 pt-2" />
                </MeasureWrapper>

                {footnotePlan.all.map((footnote) => (
                    <MeasureWrapper key={footnote.id} id={getFootnoteMeasurementId(footnote.id)}>
                        <ReportFootnoteEntry footnote={footnote} />
                    </MeasureWrapper>
                ))}
            </div>
        </div>
    );
}
